import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import styled from "styled-components";
import { CachedMicrolink } from "components/common/CachedMicrolink";
import { PostFieldLabel, PlatformUsage } from "components/Posts/PostForm";
import { PostFieldError } from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { ExternalLink } from "ui/Link";
import { FIELDS } from "constants/PostFormFields";
import { isUrl } from "utils/strings";
import filter from "lodash/filter";
import { TextInput } from "ui/ecosystems/forms/inputs";
import { Notification } from "@/components/ui/notification";
import type { FormPlatformType } from "shared/types/post";
import type { FormValidationErrors } from "shared/types/post";
import type { Platform } from "features/post/format-post";
import { getUrlUtmStatusMessage, PlatformAccountLike } from "../utils/urlUtms";

// https://microlink.io/docs/sdk/getting-started/styling
const MicrolinkWrap = styled.div`
  .microlink_card__iframe {
    iframe {
      max-width: 100%;
    }
  }
`;

interface PostUrlProps {
  url?: string | null;
  checkSetting?: boolean;
  selectedPlatform?: FormPlatformType;
  selectedAccounts?: PlatformAccountLike[];
  platforms?: Platform[];
  title: React.ReactNode | (() => React.ReactNode);
  className?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  errors?: FormValidationErrors;
  fieldName?: string;
}

const PostUrl = ({
  url,
  checkSetting = false,
  selectedPlatform,
  selectedAccounts = [],
  platforms = [],
  title,
  className = "",
  onChange,
  disabled = false,
  errors,
  fieldName = FIELDS.URL,
}: PostUrlProps) => {
  // Buffer keystrokes locally so typing doesn't dispatch a form update per
  // character; commit on a short pause, like the other text fields do.
  const [value, setValue] = useState(url || "");
  const lastCommitted = useRef(url || "");
  const containerRef = useRef<HTMLDivElement | null>(null);

  const commit = useDebouncedCallback((newValue: string) => {
    const trimmed = newValue.trim();
    if (trimmed !== lastCommitted.current) {
      lastCommitted.current = trimmed;
      onChange(trimmed);
    }
  }, 200);

  const flushPending = useCallback(() => {
    commit.flush();
    setValue((current) => current.trim());
  }, [commit]);

  useEffect(() => {
    // Resync when the url changes from outside (platform switch, post load).
    // The guard skips our own committed value echoing back, so this never
    // clobbers what the user is currently typing. Any keystrokes still pending
    // belong to the platform we just left — drop them rather than let them
    // flush onto the newly selected platform's field.
    const nextUrl = url || "";
    if (nextUrl !== lastCommitted.current) {
      commit.cancel();
      lastCommitted.current = nextUrl;
      setValue(nextUrl);
    }
  }, [url, commit]);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) {
        return;
      }

      flushPending();
    };

    document.addEventListener("mousedown", handleDocumentMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown, true);
      commit.flush();
    };
  }, [commit, flushPending]);

  const validUrl = useMemo(() => isUrl(url), [url]);
  const hasError = !!errors?.[fieldName]?.errors?.length;
  const utmStatusMessage = useMemo(
    () =>
      getUrlUtmStatusMessage({
        url,
        selectedAccounts,
        selectedPlatform,
      }),
    [url, selectedAccounts, selectedPlatform]
  );

  const skedLinkUsedForIgs = useMemo(() => {
    // Always on
    if (!checkSetting) {
      return true;
    }

    // Only check for IG
    const hasIG = filter(platforms, ["TYPE", "IG"]).length > 0;
    if (!hasIG) {
      return true;
    }

    let settingOn = true;
    for (const account of selectedAccounts) {
      const isIG =
        account.platformType === undefined || account.platformType === "IG";
      // show the prompt with information about Sked Link if user is not using it in their bio
      if (
        isIG &&
        account.igData &&
        account.igData.website &&
        !account.igData.website.includes("sked.link")
      ) {
        settingOn = false;
      }
    }
    return settingOn;
  }, [checkSetting, platforms, selectedAccounts]);

  return (
    <div ref={containerRef} className={className}>
      <div className="flex flex-col mb-4">
        <PostFieldLabel className="flex items-center mb-2">
          <div className="mr-2">
            {typeof title === "function" ? title() : title}
          </div>
          {selectedPlatform && (
            <PlatformUsage
              platforms={platforms}
              field={FIELDS.URL}
              selectedPlatform={selectedPlatform}
            />
          )}
        </PostFieldLabel>
        {/* Show URL prompt */}
        {!skedLinkUsedForIgs && (
          <Notification
            variant="info"
            title={
              <>
                <b>Add a post URL for Instagram:</b> Use our link in bio tool to
                link followers to the right place.{" "}
                <a
                  href="/sked-link-sso"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get started now
                </a>{" "}
                or find out more about{" "}
                <ExternalLink href="https://help.skedsocial.com/en/articles/3811771-getting-started-with-sked-link">
                  Sked's link in bio tool
                </ExternalLink>
                .
              </>
            }
          />
        )}
        {/* Show field for all platforms */}
        <>
          <TextInput
            className="placeholder:!text-[#808080] placeholder:!opacity-100"
            style={{
              boxSizing: "border-box",
              height: 40,
              padding: "10px 14px",
              border: "1px solid #e6e6e6",
              borderRadius: 8,
              background: disabled ? "#ebebe4" : "#ffffff",
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
              color: "#333333",
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "20px",
            }}
            value={value}
            disabled={disabled}
            error={hasError}
            onChange={(newValue: string) => {
              setValue(newValue);
              commit(newValue);
            }}
            onBlur={() => {
              // Blur fires before the click that switches platform tabs, so the
              // pending value still lands on the platform it was typed for.
              flushPending();
            }}
            onKeyDown={(event) => {
              // Cmd/Ctrl+Enter submits the post without blurring this input, so
              // commit now or the save would use the previous url.
              if (event.key === "Enter") {
                flushPending();
              }
            }}
            placeholder="https://"
          />
          {utmStatusMessage && (
            <div className="mt-2 text-xs leading-[1.4] text-neutral-500">
              {utmStatusMessage}
            </div>
          )}
          {url && validUrl && (
            <MicrolinkWrap className="my-4">
              <CachedMicrolink url={url} className="m-0" />
            </MicrolinkWrap>
          )}
          <PostFieldError
            fields={[fieldName]}
            errors={errors}
            className="mt-2"
          />
        </>
      </div>
    </div>
  );
};

export default PostUrl;
